import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContentContainer from '../../templates/Content/ContentContainer';
import {
  getLocalizedHelpCenterMeta,
  getLocalizedHelpCenterKeywords,
} from './localization';
import { getAccessibleHelpCenterContent } from './registry';
import * as s from './styles';

const normalizeText = (value: string): string => value
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const HelpCenterPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  const language = i18n.resolvedLanguage ?? i18n.language;
  const pageContent = useMemo(
    () => getAccessibleHelpCenterContent('page').map((definition) => ({
      definition,
      meta: getLocalizedHelpCenterMeta(definition, language),
      keywords: getLocalizedHelpCenterKeywords(definition, language),
    })),
    [language],
  );

  const categoryOptions = useMemo(() => Array.from(new Set(
    pageContent.map((item) => item.meta.category),
  )), [pageContent]);

  const filteredContent = useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm.trim());

    return pageContent.filter((item) => {
      const matchesCategory = selectedCategory === 'all' || item.meta.category === selectedCategory;

      if (!matchesCategory) return false;
      if (!normalizedSearch) return true;

      const searchValues = [
        item.meta.title,
        item.meta.summary,
        item.meta.category,
        ...item.meta.tags,
        ...item.keywords,
      ].map((value) => normalizeText(value));

      return searchValues.some((value) => value.includes(normalizedSearch));
    });
  }, [pageContent, searchTerm, selectedCategory]);

  useEffect(() => {
    if (!filteredContent.length) {
      setSelectedSlug(null);
      return;
    }

    const hasSelectedVisible =
      filteredContent.some((item) => item.definition.slug === selectedSlug);

    if (!hasSelectedVisible) {
      setSelectedSlug(filteredContent[0].definition.slug);
    }
  }, [filteredContent, selectedSlug]);

  const selectedContent = filteredContent.find(
    (item) => item.definition.slug === selectedSlug,
  ) ?? null;

  return (
    <ContentContainer title={t('helpCenter')}>
      <s.PageShell>
        <s.HeroPanel>
          <h2>{t('helpCenter')}</h2>
          <p>{t('helpCenterDescription')}</p>
        </s.HeroPanel>

        <s.FiltersRow>
          <s.SearchInput
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('helpCenterSearchPlaceholder')}
          />

          <s.CategorySelect
            value={selectedCategory}
            onChange={(event) => setSelectedCategory(event.target.value)}
          >
            <option value="all">{t('helpCenterAllCategories')}</option>
            {categoryOptions.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </s.CategorySelect>
        </s.FiltersRow>

        <s.ContentGrid>
          <s.ListPanel>
            <s.ListCounter>
              {t('helpCenterContentCount', { count: filteredContent.length })}
            </s.ListCounter>

            {filteredContent.map((item) => (
              <s.ContentCard
                key={item.definition.id}
                active={item.definition.slug === selectedSlug}
                onClick={() => setSelectedSlug(item.definition.slug)}
                type="button"
              >
                <s.ContentCardMeta>
                  <s.ContentMetaBadge tone="primary">{item.meta.category}</s.ContentMetaBadge>
                  {item.meta.tags.slice(0, 2).map((tag) => (
                    <s.ContentMetaBadge key={tag}>{tag}</s.ContentMetaBadge>
                  ))}
                </s.ContentCardMeta>

                <h3>{item.meta.title}</h3>
                <p>{item.meta.summary}</p>
              </s.ContentCard>
            ))}

            {!filteredContent.length && (
              <s.EmptyState>
                <h3>{t('helpCenterNoResultsTitle')}</h3>
                <p>{t('helpCenterNoResultsDescription')}</p>
              </s.EmptyState>
            )}
          </s.ListPanel>

          <s.PreviewPanel>
            {selectedContent ? (
              <selectedContent.definition.Content mode="page" />
            ) : (
              <s.EmptyState>
                <h3>{t('helpCenterSelectContentTitle')}</h3>
                <p>{t('helpCenterSelectContentDescription')}</p>
              </s.EmptyState>
            )}
          </s.PreviewPanel>
        </s.ContentGrid>
      </s.PageShell>
    </ContentContainer>
  );
};

export default HelpCenterPage;
